import { Box, Button, Typography } from '@mui/material'
import * as React from 'react'
import { colors } from '../constants/colors'
import { HBox, HeightBox, OverflowInvisibleScroll, VBox } from '../elements/basic-elements'

import AndOrFilter from './and-or-filter'
import ChipList, { Chip, ChipProps } from './chip-list'
import FilterUpdateScreen, { FilterUpdateProps } from './filter-update-screen'
import PartialScreenModal from './partial-screen-modal'

import { resultsState } from '../context/results-context'
import { filtersState } from '../state/filters-state'

import { sanityString } from '../async/sanity/utils'
import { LocationType } from '../types/location-types'
import ImpactScoreGlobes from './impact-score-globes'

type ActionSorts = 'title' | 'impact' | 'region'

let clearFilterTimeout: NodeJS.Timeout
const FilterControls: React.FC<{ onSave: Function; onEdit: Function }> = ({ onSave, onEdit }) => {
  const { changeFilters, filters } = resultsState()
  const { allFilters, filtersLoading, fetchAllFiltersIfNotFetched } = filtersState()

  type FilterSelectionState = {
    verbs: Queries.SanityVerb[]
    nouns: Queries.SanityNoun[]
    cobenefits: Queries.SanityCobenefits[]
    skills: Queries.SanitySkill[]
    endorsements: Queries.SanityOrganization[]
    issues: Queries.SanityIssue[]
    mediums: Queries.SanityMedium[]
    tags: Queries.SanityTag[]
  }

  type ExclusiveFiltersState = {
    verbsExclusive: boolean
    nounsExclusive: boolean
    cobenefitsExclusive: boolean
    skillsExclusive: boolean
    endorsementsExclusive: boolean
    issuesExclusive: boolean
  }

  type GenericFilterItem = { title?: string | null } & Record<string, unknown>

  const createInitialSelections = (): FilterSelectionState => ({
    verbs: (filters.verbs as Queries.SanityVerb[]) || [],
    nouns: (filters.nouns as Queries.SanityNoun[]) || [],
    cobenefits: (filters.cobenefits as Queries.SanityCobenefits[]) || [],
    skills: (filters.skills as Queries.SanitySkill[]) || [],
    mediums: (filters.mediums as Queries.SanityMedium[]) || [],
    tags: (filters.tags as Queries.SanityTag[]) || [],
    endorsements: (filters.endorsements as Queries.SanityOrganization[]) || [],
    issues: (filters.issues as Queries.SanityIssue[]) || []
  })

  const [filterSelections, setFilterSelections] = React.useState<FilterSelectionState>(
    createInitialSelections()
  )
  const [exclusiveFilters, setExclusiveFilters] = React.useState<ExclusiveFiltersState>({
    verbsExclusive: Boolean(filters.verbsExclusive),
    nounsExclusive: Boolean(filters.nounsExclusive),
    cobenefitsExclusive: Boolean(filters.cobenefitsExclusive),
    skillsExclusive: Boolean(filters.skillsExclusive),
    endorsementsExclusive: Boolean(filters.endorsementsExclusive),
    issuesExclusive: Boolean(filters.issuesExclusive)
  })

  // ------------------------------------------------
  // Filter options
  // ------------------------------------------------
  const [nounOptions, setNounOptions] = React.useState<Queries.SanityNoun[]>([])
  const [verbOptions, setVerbOptions] = React.useState<Queries.SanityVerb[]>([])
  const [cobenefitOptions, setCobenefitOptions] = React.useState<Queries.SanityCobenefits[]>([])
  const [skillOptions, setSkillOptions] = React.useState<Queries.SanitySkill[]>([])
  const [mediumOptions, setMediumOptions] = React.useState<Queries.SanityMedium[]>([])
  const [tagOptions, setTagOptions] = React.useState<Queries.SanityTag[]>([])
  const [endorsementOptions, setEndorsementOptions] = React.useState<Queries.SanityOrganization[]>(
    []
  )
  const [issueOptions, setIssueOptions] = React.useState<Queries.SanityIssue[]>([])

  // ------------------------------------------------
  // End Filter options
  // ------------------------------------------------
  // Modal stuff
  // ------------------------------------------------

  const [activeFilterMenu, setActiveFilterMenu] = React.useState<FilterUpdateProps>()
  const [showFilterMenu, setShowFilterMenu] = React.useState(false)
  const closeFilterMenu = () => setShowFilterMenu(false)

  // ------------------------------------------------
  // End Modal stuff
  // ------------------------------------------------
  // Active Filters
  // ------------------------------------------------

  const [levelsFilters, setLevelsFilters] = React.useState<
    { title: LocationType; active: boolean }[]
  >([
    {
      title: 'global',
      active: Boolean(filters.levels && filters.levels?.includes('global'))
    },
    {
      title: 'federal',
      active: Boolean(filters.levels && filters.levels?.includes('federal'))
    },
    {
      title: 'state',
      active: Boolean(filters.levels && filters.levels?.includes('state'))
    },
    {
      title: 'local',
      active: Boolean(filters.levels && filters.levels?.includes('city'))
    }
  ])

  const [impactsFilters, setImpactsFilters] = React.useState<
    { title: '1' | '2' | '3'; active: boolean }[]
  >([
    { title: '1', active: Boolean(filters?.impacts?.includes('1')) },
    { title: '2', active: Boolean(filters?.impacts?.includes('2')) },
    { title: '3', active: Boolean(filters?.impacts?.includes('3')) }
  ])

  const onToggleImpactChip = (chip: ChipProps) => {
    triggerEdits(() =>
      setImpactsFilters(
        impactsFilters.map(filter =>
          filter.title === chip.title ? { ...filter, active: chip.active } : filter
        )
      )
    )
  }

  const sortLabels = {
    title: 'Title',
    impact: 'Impact Score',
    region: 'Region'
  }
  const [sortFilters, setSortFilters] = React.useState<
    { title: string; type: ActionSorts; active: boolean }[]
  >([
    {
      title: sortLabels['title'],
      type: 'title',
      active: Boolean(filters.sorts && filters.sorts?.includes('title'))
    },
    {
      title: sortLabels['impact'],
      type: 'impact',
      active: Boolean(filters.sorts && filters.sorts?.includes('impact'))
    },
    {
      title: sortLabels['region'],
      type: 'region',
      active: Boolean(filters.sorts && filters.sorts?.includes('region'))
    }
  ])

  type FilterSelectionKey = keyof FilterSelectionState
  type ExclusiveSelectionKey = keyof ExclusiveFiltersState

  const createEmptySelections = (): FilterSelectionState => ({
    verbs: [],
    nouns: [],
    cobenefits: [],
    skills: [],
    mediums: [],
    tags: [],
    endorsements: [],
    issues: []
  })

  const getDisplayTitle = (item?: { title?: string | null }) => sanityString(item?.title)

  const buildSelectableOptions = (
    available: GenericFilterItem[],
    selected: GenericFilterItem[]
  ): ChipProps[] =>
    (available || []).map(option => {
      const optionTitle = getDisplayTitle(option)
      return {
        title: optionTitle,
        active: Boolean(
          (selected || []).find(selectedOption => getDisplayTitle(selectedOption) === optionTitle)
        )
      }
    })

  const mapChipsToSelection = <T extends GenericFilterItem>(chips: ChipProps[], available: T[]): T[] => {
    const mapped = chips
      .map(({ title }) => available.find(option => getDisplayTitle(option) === title))
      .filter((exists): exists is T => Boolean(exists))
    return mapped as T[]
  }

  const updateFilterSelection = (
    key: FilterSelectionKey,
    updater: (
      current: FilterSelectionState[FilterSelectionKey]
    ) => FilterSelectionState[FilterSelectionKey]
  ) => {
    triggerEdits(() =>
      setFilterSelections(prev => ({
        ...prev,
        [key]: updater(prev[key]) as FilterSelectionState[typeof key]
      }))
    )
  }

  const handleExclusiveToggle = (key: ExclusiveSelectionKey, value: boolean) => {
    triggerEdits(() =>
      setExclusiveFilters(prev => ({
        ...prev,
        [key]: value
      }))
    )
  }

  type FilterSectionConfig<K extends FilterSelectionKey = FilterSelectionKey> = {
    key: K
    title: string
    menuTitle?: string
    disableToggle?: boolean
    exclusiveKey?: ExclusiveSelectionKey
    options: GenericFilterItem[]
    loading: boolean
  }

  const filterSectionConfigs: FilterSectionConfig[] = [
    {
      key: 'verbs',
      title: 'I want to',
      menuTitle: 'I want to',
      exclusiveKey: 'verbsExclusive',
      options: verbOptions,
      loading: !filters?.verbs && filtersLoading
    },
    {
      key: 'nouns',
      title: 'subject',
      menuTitle: 'Subject',
      exclusiveKey: 'nounsExclusive',
      options: nounOptions,
      loading: !filters?.nouns && filtersLoading
    },
    {
      key: 'cobenefits',
      title: 'co-benefits',
      menuTitle: 'Co-benefits',
      exclusiveKey: 'cobenefitsExclusive',
      options: cobenefitOptions,
      loading: !filters?.cobenefits && filtersLoading
    },
    {
      key: 'skills',
      title: 'skills',
      menuTitle: 'Skills',
      exclusiveKey: 'skillsExclusive',
      options: skillOptions,
      loading: !filters?.skills && filtersLoading
    },
    {
      key: 'endorsements',
      title: 'endorsements',
      menuTitle: 'Endorsements',
      exclusiveKey: 'endorsementsExclusive',
      options: endorsementOptions,
      loading: !filters?.endorsements && filtersLoading
    },
    {
      key: 'issues',
      title: 'issues',
      menuTitle: 'Issues',
      exclusiveKey: 'issuesExclusive',
      options: issueOptions,
      loading: !filters?.issues && filtersLoading
    },
    {
      key: 'mediums',
      title: 'mediums',
      menuTitle: 'Mediums',
      disableToggle: true,
      options: mediumOptions,
      loading: !filters?.mediums && filtersLoading
    },
    {
      key: 'tags',
      title: 'tags',
      menuTitle: 'Tags',
      disableToggle: true,
      options: tagOptions,
      loading: !filters?.tags && filtersLoading
    }
  ]

  const renderFilterSection = <K extends FilterSelectionKey>({
    key,
    title,
    menuTitle,
    disableToggle,
    exclusiveKey,
    options,
    loading
  }: FilterSectionConfig<K>) => {
    const selectedItems = filterSelections[key]
    // .map((props) => {
      // return {...props, title: capitalizeFirstLetter(props.title)}
    // })
    // console.log(selectedItems)
    const optionList = (options || []) as GenericFilterItem[]
    return (
      <AndOrFilter
        key={key}
        title={title}
        items={selectedItems}
        on={exclusiveKey ? exclusiveFilters[exclusiveKey] : undefined}
        loading={loading}
        disableToggle={disableToggle}
        onToggle={
          exclusiveKey ? isOn => handleExclusiveToggle(exclusiveKey, isOn) : undefined
        }
        onAddClick={() => {
          const selectableOptions = buildSelectableOptions(
            optionList,
            (selectedItems as GenericFilterItem[]) || []
          )
          setActiveFilterMenu({
            title: menuTitle || title,
            options: selectableOptions,
            onSave: chips =>
              updateFilterSelection(
                key,
                () =>
                  mapChipsToSelection(chips, optionList) as FilterSelectionState[FilterSelectionKey]
              )
          })
        }}
        onDeleteClick={item =>
          updateFilterSelection(key, current =>
            current.filter(
              option => getDisplayTitle(option) !== item.title
            ) as FilterSelectionState[FilterSelectionKey]
          )
        }
      />
    )
  }

  const clearAllFilters = () => {
    setExclusiveFilters({
      verbsExclusive: false,
      nounsExclusive: false,
      cobenefitsExclusive: false,
      skillsExclusive: false,
      endorsementsExclusive: false,
      issuesExclusive: false
    })
    setFilterSelections(createEmptySelections())
    setLevelsFilters(levelsFilters.map(filter => ({ ...filter, active: false })))
    setSortFilters(sortFilters.map(filter => ({ ...filter, active: false })))
  }

  const saveChanges = React.useCallback(async () => {
    const activeLevelFilters = levelsFilters
      .filter(({ active }) => active)
      .map(({ title }) => title)
    const activeSortFilters = sortFilters.filter(({ active }) => active).map(({ type }) => type)
    const activeImpactFilters = impactsFilters
      .filter(({ active }) => active)
      .map(({ title }) => title)
    const { verbs, nouns, cobenefits, skills, mediums, tags, endorsements, issues } =
      filterSelections
    const {
      verbsExclusive,
      nounsExclusive,
      cobenefitsExclusive,
      skillsExclusive,
      endorsementsExclusive,
      issuesExclusive
    } = exclusiveFilters
    const newFilters = {
      ...filters,
      nouns: nouns.length > 0 && nouns,
      verbs: verbs.length > 0 && verbs,
      cobenefits: cobenefits.length > 0 && cobenefits,
      skills: skills.length > 0 && skills,
      mediums: mediums.length > 0 && mediums,
      tags: tags.length > 0 && tags,
      endorsements: endorsements.length > 0 && endorsements,
      issues: issues.length > 0 && issues,
      impacts: activeImpactFilters.length > 0 && activeImpactFilters,
      levels: activeLevelFilters.length > 0 && activeLevelFilters,
      sorts: activeSortFilters.length > 0 && activeSortFilters,
      verbsExclusive,
      nounsExclusive,
      cobenefitsExclusive,
      skillsExclusive,
      endorsementsExclusive,
      issuesExclusive
    }

    changeFilters(newFilters)

    onSave()
  }, [
    filters,
    filterSelections,
    impactsFilters,
    levelsFilters,
    sortFilters,
    exclusiveFilters
  ])

  // ------------------------------------------------
  // End Active Filters
  // ------------------------------------------------
  // Effects
  // ------------------------------------------------

  // get filters on initial load
  React.useEffect(() => {
    fetchAllFiltersIfNotFetched()
  }, [])

  React.useEffect(() => {
    if (allFilters) {
      setNounOptions(allFilters.nouns || [])
      setVerbOptions(allFilters.verbs || [])
      setCobenefitOptions(allFilters.cobenefits || [])
      setSkillOptions(allFilters.skills || [])
      setMediumOptions(allFilters.mediums || [])
      setTagOptions(allFilters.tags || [])
      setEndorsementOptions(allFilters.endorsements || [])
      // @ts-expect-error: existing type issue
      setIssueOptions(allFilters.issues || [])
    }
  }, [allFilters])

  // Modal opening/closing
  React.useEffect(() => {
    if (activeFilterMenu && !showFilterMenu) {
      setShowFilterMenu(true)

      // kill the timeout if user selects another filter menu before
      // the current one has closed
      clearTimeout(clearFilterTimeout)
    }
  }, [activeFilterMenu])

  React.useEffect(() => {
    if (activeFilterMenu && !showFilterMenu) {
      clearFilterTimeout = setTimeout(() => setActiveFilterMenu(null), 1000)
    }
  }, [showFilterMenu])

  // ------------------------------------------------
  // End Effects
  // ------------------------------------------------

  const [hasEdited, setHasEdited] = React.useState(false)
  const triggerEdits = (callback: Function) => {
    setHasEdited(true)
    callback()
    onEdit && onEdit()
  }

  return (
    <VBox flex={1} sx={{ height: '100%' }}>
      <HBox pt={3} pb={1} alignItems="center">
        <HBox sx={{ flex: 1 }}>
          <Button
            variant="text"
            color="info"
            sx={{ color: colors.BLACK_50 }}
            onClick={clearAllFilters}
          >
            <Typography variant="body2">Clear All</Typography>
          </Button>
        </HBox>
        <Typography variant="h4" color="textPrimary">
          Filters
        </Typography>
        <Box sx={{ flex: 1 }}></Box>
      </HBox>
      <HeightBox flex={1} px={2}>
        <OverflowInvisibleScroll sx={{ maxHeight: '100%' }}>
          <VBox gap={1} pb={1}>
            {filterSectionConfigs.map(config => renderFilterSection(config))}
            <ChipList
              title="impact"
              items={impactsFilters}
              onToggle={() => {}}
              renderChip={({ title, active }) => {
                return (
                  <Chip
                    active={active}
                    onClick={() => onToggleImpactChip({ title, active: !active })}
                  >
                    <ImpactScoreGlobes rank={Number(title)} />
                  </Chip>
                )
              }}
            />
            <ChipList
              title="levels"
              items={levelsFilters}
              onToggle={chip => {
                const levelsFiltersCopy = [...levelsFilters]
                const index = levelsFiltersCopy.findIndex(c => c.title === chip.title)
                levelsFiltersCopy[index].active = chip.active
                triggerEdits(() => setLevelsFilters(levelsFiltersCopy))
              }}
            />
            <ChipList
              title="Sort By"
              items={sortFilters}
              onToggle={chip => {
                const sortFiltersCopy = [
                  ...sortFilters.map(filter => ({ ...filter, active: false }))
                ]
                const index = sortFiltersCopy.findIndex(c => c.title === chip.title)
                sortFiltersCopy[index].active = chip.active
                triggerEdits(() => setSortFilters(sortFiltersCopy))
              }}
            />
          </VBox>
        </OverflowInvisibleScroll>
      </HeightBox>
      <Box flex={0} p={2} pt={1}>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={!hasEdited}
          color="primary"
          onClick={saveChanges}
        >
          Apply Changes
        </Button>
      </Box>

      <PartialScreenModal open={showFilterMenu} onClose={closeFilterMenu}>
        {activeFilterMenu && <FilterUpdateScreen {...activeFilterMenu} onClose={closeFilterMenu} />}
      </PartialScreenModal>
    </VBox>
  )
}

export default FilterControls
